import { Link } from 'react-router-dom';
import { Check, Sparkles, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { cn } from '../ui/utils';

function renderBullet(bullet, idx) {
  if (typeof bullet === 'string') {
    return (
      <li key={idx} className="flex items-start gap-3 text-sm leading-6 text-slate-700">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
        <span>{bullet}</span>
      </li>
    );
  }

  return (
    <li key={idx} className="space-y-2">
      <div className="flex items-start gap-3 text-sm font-semibold leading-6 text-slate-900">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
        <span>{bullet.text}</span>
      </div>
      {Array.isArray(bullet.subItems) && bullet.subItems.length > 0 && (
        <ul className="space-y-1.5 pl-7">
          {bullet.subItems.map((sub, subIdx) => (
            <li
              key={subIdx}
              className="flex items-start gap-2 text-sm leading-6 text-slate-600"
            >
              <MessageCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
              <span>{sub}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function Plans({ copy }) {
  return (
    <section id="planos" className="space-y-8 scroll-mt-24">
      <div className="max-w-3xl">
        <p className="text-sm font-semibold uppercase tracking-[0.24em] text-blue-700">
          {copy.eyebrow}
        </p>
        <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          {copy.title}
        </h2>
        <p className="mt-4 text-base leading-7 text-slate-600">
          {copy.description}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 md:items-stretch">
        {copy.items.map((plan) => {
          const isPro = plan.highlighted;
          const ctaLabel = isPro && copy.ctaPro ? copy.ctaPro : copy.cta;

          return (
            <Card
              key={plan.name}
              className={cn(
                'relative rounded-[28px] border-slate-200 bg-white shadow-sm transition',
                isPro &&
                  'border-blue-300 shadow-xl shadow-blue-100/70 ring-1 ring-blue-200/70'
              )}
            >
              {isPro && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-700 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-sm">
                    <Sparkles className="h-3.5 w-3.5" />
                    {plan.badge}
                  </span>
                </div>
              )}

              <CardContent className="flex h-full flex-col gap-6 p-8">
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-2xl font-semibold text-slate-900">
                      {plan.name}
                    </p>
                    {!isPro && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                        {plan.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">{plan.description}</p>
                </div>

                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold tracking-tight text-slate-900">
                    {plan.price}
                  </span>
                  {plan.cycle && (
                    <span className="text-sm font-medium text-slate-500">
                      {plan.cycle}
                    </span>
                  )}
                </div>

                {Array.isArray(plan.bullets) && plan.bullets.length > 0 && (
                  <ul className="flex-1 space-y-3 border-t border-slate-100 pt-6">
                    {plan.bullets.map(renderBullet)}
                  </ul>
                )}

                <Button
                  asChild
                  className={cn(
                    'mt-2 w-full',
                    isPro
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800'
                      : 'bg-slate-900 hover:bg-slate-800'
                  )}
                >
                  <Link to={`/signin?mode=register&plan=${isPro ? 'pro' : 'free'}`}>
                    {ctaLabel}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </section>
  );
}
